import { rainbowViewingDirection, solarPosition } from "./solar";

// ─── Open-Meteo API Types ────────────────────────────────────────────────────

export interface OpenMeteoCurrent {
	time: string;
	interval: number;
	temperature_2m: number;
	relative_humidity_2m: number;
	precipitation: number;
	weather_code: number;
	cloud_cover: number;
	wind_speed_10m: number;
}

export interface OpenMeteoHourly {
	time: string[];
	temperature_2m: (number | null)[];
	relative_humidity_2m: (number | null)[];
	precipitation: (number | null)[];
	precipitation_probability: (number | null)[];
	cloud_cover: (number | null)[];
	weather_code: (number | null)[];
	wind_speed_10m: (number | null)[];
}

export interface OpenMeteoDaily {
	time: string[];
	sunrise: string[];
	sunset: string[];
	precipitation_probability_max: (number | null)[];
	weather_code: (number | null)[];
	temperature_2m_max: (number | null)[];
	temperature_2m_min: (number | null)[];
}

export interface OpenMeteoResponse {
	latitude: number;
	longitude: number;
	timezone: string;
	timezone_abbreviation: string;
	utc_offset_seconds: number;
	elevation: number;
	current: OpenMeteoCurrent;
	hourly: OpenMeteoHourly;
	daily: OpenMeteoDaily;
}

// ─── Rainbow Assessment Types ────────────────────────────────────────────────

export interface RainbowAssessment {
	score: number; // 0–100
	sunAltitude: number; // degrees above horizon
	sunAzimuth: number; // degrees from north
	viewDirection: { degrees: number; bearing: string } | null;
	isDaytime: boolean;
}

export interface HourlyAssessment {
	time: string;
	date: Date;
	score: number;
	assessment: RainbowAssessment;
	precipitation: number;
	precipitationProbability: number;
	cloudCover: number;
	temperature: number;
	humidity: number;
	weatherCode: number;
}

export interface DailyPeak {
	date: string;
	dayName: string;
	peakScore: number;
	peakTime: string;
	peakHour: number;
	weatherCode: number;
	tempMax: number;
	tempMin: number;
	sunrise: string;
	sunset: string;
	precipProbMax: number;
}

// ─── Rainbow Score Calculation ───────────────────────────────────────────────
//
// Key physics:
//   • Primary rainbow at 42° from antisolar point (opposite the sun).
//   • Sun must be above horizon and below ~42° altitude for the rainbow arc
//     to appear above the horizon.
//   • Best conditions: low sun angle (5–25°) + active/showery precipitation +
//     broken cloud cover (40–65%) allowing direct sunlight.
//   • Hawaii advantage: trade-wind showers + strong tropical sun = frequent
//     short-lived rainbow windows, especially in morning and late afternoon.

export function calculateRainbowScore(
	date: Date,
	latitude: number,
	longitude: number,
	precipitationProbability: number,
	precipitation: number,
	cloudCover: number,
	humidity: number,
	recentPrecipitation: number = 0,
): RainbowAssessment {
	const sun = solarPosition(date, latitude, longitude);
	const isDaytime = sun.altitude > 0;

	if (!isDaytime) {
		return {
			score: 0,
			sunAltitude: sun.altitude,
			sunAzimuth: sun.azimuth,
			viewDirection: null,
			isDaytime: false,
		};
	}

	// ── Sun altitude factor ──
	let sunScore: number;
	if (sun.altitude < 3) sunScore = (sun.altitude / 3) * 0.5;
	else if (sun.altitude <= 20) sunScore = 1.0;
	else if (sun.altitude <= 35)
		sunScore = 1.0 - ((sun.altitude - 20) / 15) * 0.4;
	else if (sun.altitude <= 42) sunScore = 0.6 - ((sun.altitude - 35) / 7) * 0.5;
	else sunScore = Math.max(0, 0.1 - ((sun.altitude - 42) / 10) * 0.1);

	// ── Precipitation factor ──
	const totalPrecip = precipitation + recentPrecipitation * 0.5;
	let precipScore: number;
	if (totalPrecip > 1.0) precipScore = 1.0;
	else if (totalPrecip > 0.3) precipScore = 0.85;
	else if (totalPrecip > 0.1) precipScore = 0.7;
	else if (precipitationProbability >= 70) precipScore = 0.75;
	else if (precipitationProbability >= 50) precipScore = 0.6;
	else if (precipitationProbability >= 30) precipScore = 0.4;
	else if (precipitationProbability >= 15) precipScore = 0.2;
	else precipScore = (precipitationProbability / 15) * 0.1;

	// ── Cloud cover factor (broken clouds = ideal) ──
	let cloudScore: number;
	if (cloudCover < 15) cloudScore = 0.15;
	else if (cloudCover < 30) cloudScore = 0.45;
	else if (cloudCover < 50) cloudScore = 0.8;
	else if (cloudCover < 65) cloudScore = 1.0;
	else if (cloudCover < 80) cloudScore = 0.6;
	else if (cloudCover < 92) cloudScore = 0.25;
	else cloudScore = 0.05;

	// ── Humidity bonus ──
	let humidBonus = 0;
	if (humidity > 85) humidBonus = 0.12;
	else if (humidity > 75) humidBonus = 0.08;
	else if (humidity > 65) humidBonus = 0.04;

	// ── Combined weighted score ──
	const raw =
		sunScore * 0.3 + precipScore * 0.42 + cloudScore * 0.28 + humidBonus;
	const score = Math.min(100, Math.max(0, Math.round(raw * 100)));

	const viewDirection =
		sun.altitude > 0 ? rainbowViewingDirection(sun.azimuth) : null;

	return {
		score,
		sunAltitude: sun.altitude,
		sunAzimuth: sun.azimuth,
		viewDirection,
		isDaytime: true,
	};
}

// ─── WMO Weather Code Helpers ────────────────────────────────────────────────

export function wmoDescription(code: number): string {
	const d: Record<number, string> = {
		0: "Clear sky",
		1: "Mainly clear",
		2: "Partly cloudy",
		3: "Overcast",
		45: "Foggy",
		48: "Rime fog",
		51: "Light drizzle",
		53: "Moderate drizzle",
		55: "Dense drizzle",
		56: "Freezing drizzle",
		57: "Dense freezing drizzle",
		61: "Slight rain",
		63: "Moderate rain",
		65: "Heavy rain",
		66: "Freezing rain",
		67: "Heavy freezing rain",
		71: "Light snow",
		73: "Moderate snow",
		75: "Heavy snow",
		77: "Snow grains",
		80: "Light showers",
		81: "Moderate showers",
		82: "Violent showers",
		85: "Light snow showers",
		86: "Heavy snow showers",
		95: "Thunderstorm",
		96: "Thunderstorm + hail",
		99: "Thunderstorm + heavy hail",
	};
	return d[code] ?? "Unknown";
}

export function wmoIcon(code: number): string {
	if (code === 0) return "☀️";
	if (code <= 2) return "⛅";
	if (code === 3) return "☁️";
	if (code <= 48) return "🌫️";
	if (code <= 57) return "🌧️";
	if (code <= 67) return "🌧️";
	if (code <= 77) return "🌨️";
	if (code <= 82) return "🌦️";
	if (code <= 86) return "🌨️";
	return "⛈️";
}

// ─── Batch Assessment Helpers ────────────────────────────────────────────────

export function assessAllHours(
	data: OpenMeteoResponse,
	latitude: number,
	longitude: number,
): HourlyAssessment[] {
	const result: HourlyAssessment[] = [];
	const { hourly } = data;

	for (let i = 0; i < hourly.time.length; i++) {
		const precipProb = hourly.precipitation_probability[i] ?? 0;
		const precip = hourly.precipitation[i] ?? 0;
		const cloud = hourly.cloud_cover[i] ?? 0;
		const humid = hourly.relative_humidity_2m[i] ?? 50;
		const recentPrecip = i > 0 ? (hourly.precipitation[i - 1] ?? 0) : 0;
		const date = new Date(hourly.time[i]!);

		const assessment = calculateRainbowScore(
			date,
			latitude,
			longitude,
			precipProb,
			precip,
			cloud,
			humid,
			recentPrecip,
		);

		result.push({
			time: hourly.time[i]!,
			date,
			score: assessment.score,
			assessment,
			precipitation: precip,
			precipitationProbability: precipProb,
			cloudCover: cloud,
			temperature: hourly.temperature_2m[i] ?? 0,
			humidity: humid,
			weatherCode: hourly.weather_code[i] ?? 0,
		});
	}

	return result;
}

export function findDailyPeaks(
	data: OpenMeteoResponse,
	assessments: HourlyAssessment[],
): DailyPeak[] {
	const peaks: DailyPeak[] = [];
	const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

	for (let d = 0; d < data.daily.time.length; d++) {
		const dayStr = data.daily.time[d]!;
		const dayDate = new Date(dayStr + "T12:00:00");

		let bestScore = 0;
		let bestTime = "";
		let bestHour = 12;
		let bestWeatherCode = 0;

		for (const a of assessments) {
			if (a.time.startsWith(dayStr) && a.score > bestScore) {
				bestScore = a.score;
				bestTime = a.time;
				bestHour = a.date.getHours();
				bestWeatherCode = a.weatherCode;
			}
		}

		peaks.push({
			date: dayStr,
			dayName: dayNames[dayDate.getDay()] ?? "",
			peakScore: bestScore,
			peakTime: bestTime,
			peakHour: bestHour,
			weatherCode: bestWeatherCode,
			tempMax: data.daily.temperature_2m_max[d] ?? 0,
			tempMin: data.daily.temperature_2m_min[d] ?? 0,
			sunrise: data.daily.sunrise[d] ?? "",
			sunset: data.daily.sunset[d] ?? "",
			precipProbMax: data.daily.precipitation_probability_max[d] ?? 0,
		});
	}

	return peaks;
}

// ─── Score → Visual Helpers ──────────────────────────────────────────────────

export function scoreLabel(score: number): string {
	if (score >= 80) return "Prime";
	if (score >= 60) return "Strong";
	if (score >= 40) return "Possible";
	if (score >= 20) return "Marginal";
	if (score >= 8) return "Low";
	return "Quiet";
}

export function scoreColor(score: number): string {
	if (score >= 80) return "text-violet-400";
	if (score >= 60) return "text-emerald-400";
	if (score >= 40) return "text-amber-400";
	if (score >= 20) return "text-orange-400";
	return "text-slate-400";
}

export function scoreBarColor(score: number): string {
	if (score >= 80) return "bg-violet-500";
	if (score >= 60) return "bg-emerald-500";
	if (score >= 40) return "bg-amber-500";
	if (score >= 20) return "bg-orange-500";
	return "bg-slate-600";
}
